import moment from "moment";
import { logError } from "utils/error";
import { is_empty } from "utils/validations";
import constants from "constants/constants";
import {
  CREATE_PAYMENT_LINK_FORM_PAGES,
  LINK_TYPES,
  OFFERING_FIELD_DEFAULT_VALUES,
} from "../constants/CreatePaymentLinkModal.constants";
import { ICreatePaymentLinkFormHoc } from "../modules/CreatePaymentLinkFormHoc/interfaces/ICreatePaymentLinkFormHoc";
import {
  createComboPaymentLink,
  createPaymentLink,
  updatePaymentLink,
} from "./CreatePaymentLinkModal.apiCalls";
import {
  constants as webinatConstants,
  enums,
} from "schedule-v2/Webinar/constants";
import { webinar_event_type } from "schedule-v2/constants";
import { checkIsDefined } from "features/Common/modules/DataTypes/utils/nanNullUndef";
import {
  isBrandedCommunityOffering,
  isClassType,
  isCommunityType,
  isMerchandise,
} from "utils/listing/listingUtils";
import { isRequestSuccessful } from "utils/Utils";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { FETCH_PAYMENT_LINK } from "features/PaymentLinks/constants/PaymentLinks.apis";

export const generateLinkTypeOption = ({
  title,
  icon,
  description,
  value,
}: {
  title: string;
  description: string;
  icon: string;
  value: number;
}) => {
  return {
    title,
    description,
    icon,
    value,
  };
};

export const isLinkTypeSelectionPage = (page: number) =>
  page === CREATE_PAYMENT_LINK_FORM_PAGES.LINK_TYPE_SELECTION;

export const isLinkCreationFormInputsPage = (page: number) =>
  page === CREATE_PAYMENT_LINK_FORM_PAGES.FORM_INPUTS;

export const isCustomPaymentLinkType = (type: number) =>
  type === LINK_TYPES.CUSTOM_LINK;

export const isOpenPaymentLinkType = (type: number) =>
  type === LINK_TYPES.OPEN_LINK;

export const isComboPaymentLinkType = (type: number) =>
  type === LINK_TYPES.COMBO_LINK;

export const handleDateTime = (date: string, time: string) => {
  if (is_empty(date)) return undefined;
  const _date = moment(date).format("YYYY-MM-DD");
  const _time = is_empty(time)
    ? moment().startOf("day").format("HH:mm:ss")
    : time;
  return moment(_date + " " + _time).toISOString();
};

export const handleCreatePaymentLink = async (
  formData: ICreatePaymentLinkFormHoc.IFormState,
  onSuccess?: () => void,
  // eslint-disable-next-line no-unused-vars
  onError?: (error: any) => void
) => {
  const {
    link_price: linkPrice,
    currency,
    min_set_price,
    expiry_date,
    expiry_time,
    community_channels_payload,
    membership_uuid,
    membership_variant_uuid,
    payment_link_type,
    title,
    selectedListing,
    record,
    listingsPayload,
    booking_fee_type,
  } = formData;

  const metadata = {
    ...(booking_fee_type !== undefined && { ihf_included: booking_fee_type }),
  };

  const commonPayload = {
    title: title?.trim(),
    expiry_date: handleDateTime(expiry_date, expiry_time),
  };

  try {
    let paymentLink = "";
    let listingUuid = "";
    let dynamicLinkUuid = "";

    if (record) {
      // editing payment link
      const { dynamic_link_id: dynamicLinkId } = record;
      const updatePayload = {
        ...commonPayload,
        ...(Object.keys(metadata).length && { metadata }),
      };
      await updatePaymentLink(dynamicLinkId, updatePayload);
    } else if (selectedListing) {
      // creating new payment link

      const offeringVariantUuid = selectedListing.is_offering_variants_enabled
        ? listingsPayload?.[0]?.offering_variant_uuid
        : undefined;
      const offeringVariantTitle = selectedListing.is_offering_variants_enabled
        ? listingsPayload?.[0]?.offering_variant_title
        : undefined;
      const isValidVariant =
        offeringVariantUuid &&
        !Object.values(OFFERING_FIELD_DEFAULT_VALUES).includes(
          offeringVariantUuid
        );

      if (
        payment_link_type === LINK_TYPES.CUSTOM_LINK &&
        selectedListing.is_offering_variants_enabled &&
        !isValidVariant &&
        !isBrandedCommunityOffering(selectedListing.type)
      ) {
        throw new Error("Please select a plan for the offering");
      }

      const response = await createPaymentLink({
        listing_id: selectedListing.id,
        link_price: checkIsDefined(linkPrice) ? +linkPrice! : linkPrice,
        currency: isOpenPaymentLinkType(payment_link_type)
          ? undefined
          : currency,
        community_channels_payload,
        membership_uuid,
        membership_variant_uuid,
        payment_link_type,
        ...(min_set_price && {
          min_set_price: parseFloat(`${min_set_price}`),
        }),
        ...commonPayload,
        ...(Object.keys(metadata).length && { metadata }),
        ...(isValidVariant && {
          offering_variant_uuid: offeringVariantUuid,
          offering_variant_title: offeringVariantTitle,
        }),
      });
      if (isRequestSuccessful(response.status)) {
        paymentLink = response?.data?.pricing_link;
        listingUuid = response?.data?.listing_uuid;
        dynamicLinkUuid = response?.data?.dynamic_link_id;
      }
    } else if (payment_link_type === LINK_TYPES.COMBO_LINK) {
      if (payment_link_type === LINK_TYPES.COMBO_LINK) {
        const response = await createComboPaymentLink({
          listings: sanitizeListingPayload(listingsPayload) || [],
          link_price: checkIsDefined(linkPrice) ? +linkPrice! : 0,
          currency: isOpenPaymentLinkType(payment_link_type) ? "" : currency,
          payment_link_type: LINK_TYPES.CUSTOM_LINK,
          ...commonPayload,
          ...(Object.keys(metadata).length && { metadata }),
        });

        if (isRequestSuccessful(response.status)) {
          paymentLink = response?.data?.pricing_link;
          dynamicLinkUuid = response?.data?.dynamic_link_id;
        }
      }
    }

    if (onSuccess) onSuccess();

    return { paymentLink, listingUuid, dynamicLinkUuid };
  } catch (error) {
    logError({
      error,
      occuredAt:
        "src/features/Crm/modules/PaymentLinks/modules/CreatePaymentLinkModal/modules/CreatePaymentLinkFormHoc/utils/CreatePaymentLinkFormHoc.utils.ts",
      when: "calling createPaymentLink",
      severity: "blocker",
    });

    if (onError) onError(error);

    throw error;
  }
};

const sanitizeListingPayload = (
  listingPayload: ICreatePaymentLinkFormHoc.IFormState["listingsPayload"]
): {
  key: string;
  uuid: string;
  allow_booking_next_available_session: boolean;
  allow_booking_next_available_batch: boolean;
  quantity?: number | string;
  slot_uuid?: string;
  class_uuid?: string;
  limit_sessions?: boolean;
  sessions_visible_on_booking_page?: number;
  offering_variant_uuid?: string;
  offering_variant_title?: string;
}[] => {
  if (checkIsArrayEmpty(listingPayload)) return [];

  return (
    (listingPayload || [])
      .map((listing) => {
        if (!listing) return undefined;

        const payload = { ...listing };

        // Remove each optional field independently if it holds a default placeholder value
        if (
          Object.values(OFFERING_FIELD_DEFAULT_VALUES).includes(
            payload.class_uuid || ""
          )
        ) {
          delete payload.class_uuid;
        }
        if (
          Object.values(OFFERING_FIELD_DEFAULT_VALUES).includes(
            payload.slot_uuid || ""
          )
        ) {
          delete payload.slot_uuid;
        }
        if (
          typeof payload.quantity === "string" &&
          Object.values(OFFERING_FIELD_DEFAULT_VALUES).includes(
            payload.quantity
          )
        ) {
          delete payload.quantity;
        }
        if (
          Object.values(OFFERING_FIELD_DEFAULT_VALUES).includes(
            payload.offering_variant_uuid || ""
          )
        ) {
          delete payload.offering_variant_uuid;
          delete payload.offering_variant_title;
        }

        // These fields are optional and should not be sent to the backend as they carry local state data is case of branded community
        if (payload.is_community) {
          delete payload.is_community;
          delete payload.title;
          delete payload.community_membership_channel;
          delete payload.quantity;
        }

        return payload;
      })
      // Removes any `undefined` values returned from map
      // and narrow the type so that the returned array matches the expected payload type exactly
      .filter((item): item is NonNullable<typeof item> => item !== undefined)
  );
};

export const desanitizeListingPayload = (
  sanitizedPayload: ICreatePaymentLinkFormHoc.IFormState["listingsPayload"]
) => {
  return (sanitizedPayload || []).map((listing) => {
    const isMultiDayEvent =
      listing?.metadata?.webinar_event_type ===
      webinar_event_type.MULTI_DAY_EVENT;
    const isWebinarMultiDayRepeatedOnSelectedDayEvent =
      listing?.type === constants.scheduleTypesId.webinar &&
      listing?.availability_choice ===
        webinatConstants.session_types[enums.OCCUR_ON_SELECTED_DAYS_OF_WEEK] &&
      isMultiDayEvent;

    const isMultiDayWebinarOrClassTypeListing =
      isMultiDayEvent || isClassType(listing?.type);

    let classUuid = "";
    if (isMultiDayWebinarOrClassTypeListing) {
      classUuid = listing?.allow_booking_next_available_batch
        ? OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_NEXT_AVAILABLE_BATCH
        : listing?.class_uuid ||
          OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_BATCH;
    }

    return {
      ...listing,
      class_uuid: classUuid, // Add default only if `class_uuid` is missing
      slot_uuid: listing?.allow_booking_next_available_session
        ? OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_SESSION_AVAILABLE_SESSION
        : listing?.slot_uuid ||
          OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_DEFAULT, // Add default only if `slot_uuid` is missing
      ...(isWebinarMultiDayRepeatedOnSelectedDayEvent && {
        selected_option_key: listing?.class_uuid
          ? `${listing?.class_uuid}-${listing?.slot_uuids?.at(0)}`
          : listing?.allow_booking_next_available_batch
          ? OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_NEXT_AVAILABLE_BATCH
          : OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_BATCH,
      }),
      ...(isMerchandise(listing?.type) && {
        quantity:
          listing?.quantity ||
          OFFERING_FIELD_DEFAULT_VALUES.PRODUCT_QUANTITY_DEFAULT,
      }),
      offering_variant_uuid:
        listing?.offering_variant_uuid ||
        OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_VARIATION,
      offering_variant_title:
        listing?.offering_variant_title ||
        OFFERING_FIELD_DEFAULT_VALUES.ALLOW_CUSTOMER_CHOOSE_VARIATION,
    };
  });
};

export const useInitializeEditingPaymentLink = (
  uuid: string,
  enabled: boolean
) => {
  return useCustomQuery({
    url: `${FETCH_PAYMENT_LINK}/${uuid}`,
    params: {},
    queryKey: `${FETCH_PAYMENT_LINK}/${uuid}`,
    cacheTime: 0,
    enabled,
  });
};
